import { Component } from '@theme/component';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Parses the merchant-entered deadline as a local wall-clock time.
 *
 * `Date.parse` is not usable here: it reads a bare `YYYY-MM-DD` as UTC but
 * `YYYY-MM-DDTHH:MM` as local, so the same setting would shift by the visitor's
 * offset depending on whether they typed a time. Building the Date from parts
 * keeps it local in every case.
 *
 * @param {string} value - e.g. "2026-09-01 18:00" or "2026-09-01"
 * @returns {Date | null} null when the value cannot be understood.
 */
function parseDeadline(value) {
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s](\d{1,2}):(\d{1,2}))?$/.exec(value.trim());
  if (!match) return null;

  const [, year, month, day, hours = '0', minutes = '0'] = match;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes)
  );

  if (Number.isNaN(date.getTime())) return null;

  // The Date constructor silently rolls out-of-range parts over, so "2026-13-45"
  // would become February 2027. Reject anything that did not survive the round
  // trip - a hidden countdown beats a confidently wrong deadline.
  const roundTrips =
    date.getFullYear() === Number(year) &&
    date.getMonth() === Number(month) - 1 &&
    date.getDate() === Number(day) &&
    date.getHours() === Number(hours) &&
    date.getMinutes() === Number(minutes);

  return roundTrips ? date : null;
}

/**
 * A countdown to a fixed date, ticking once per second.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} body - Wrapper around the ticking figures.
 * @property {HTMLElement} expired - Message shown once the deadline passes.
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 *
 * @extends Component<Refs>
 */
class CountdownTimer extends Component {
  requiredRefs = ['body', 'expired', 'days', 'hours', 'minutes', 'seconds'];

  /** @type {number | undefined} */
  #timer;

  /** @type {Date | null} */
  #deadline = null;

  connectedCallback() {
    super.connectedCallback();

    this.#deadline = parseDeadline(this.dataset.endAt ?? '');

    // A missing or malformed date should not leave a row of dashes on the page.
    if (!this.#deadline) {
      this.hidden = true;
      return;
    }

    this.#tick();
    this.#timer = setInterval(() => this.#tick(), SECOND);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
  }

  #stop() {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = undefined;
  }

  #tick() {
    if (!this.#deadline) return;

    const remaining = this.#deadline.getTime() - Date.now();

    if (remaining <= 0) {
      this.#expire();
      return;
    }

    const { days, hours, minutes, seconds } = this.refs;

    days.textContent = String(Math.floor(remaining / DAY)).padStart(2, '0');
    hours.textContent = pad(Math.floor(remaining / HOUR) % 24);
    minutes.textContent = pad(Math.floor(remaining / MINUTE) % 60);
    seconds.textContent = pad(Math.floor(remaining / SECOND) % 60);
  }

  #expire() {
    this.#stop();

    const { body, expired } = this.refs;
    body.hidden = true;

    // With no message to fall back on, drop the whole block rather than leaving
    // an empty gap in the layout.
    if (expired.textContent?.trim()) {
      expired.hidden = false;
    } else {
      this.hidden = true;
    }
  }
}

/**
 * @param {number} value
 * @returns {string}
 */
function pad(value) {
  return String(value).padStart(2, '0');
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
